import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  Checkbox,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import DoneAllOutlinedIcon from '@material-ui/icons/DoneAllOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import { RegistroMarcacion, useGestionMarcacionApi } from '../hooks/useGestionMarcacionApi';
import { toISODate, marcarGestionEnGestionErrores } from '../utils/marcacionUtils';
import MarcacionesDiaPanel from './MarcacionesDiaPanel';

// Ventana única para corregir SIN-ENTRADA / SIN-SALIDA. Se usa desde las pestañas
// "Por Punto de Servicio" y "Pendientes" de Gestión de Errores, así ambas ofrecen
// las mismas acciones:
// - Guardar hora: crea la marcación pareja faltante (entra a la cola del proceso de horas).
// - Crear lápiz: deja el caso para gestionarlo en EMSYS.
// - Cambiar tipo: la marcación existente se registró como HE/HS equivocado.
// - Corregir par: elegir 2 marcaciones del día en el PS (menor hora = HE, mayor = HS).

type Modo = 'NORMAL' | 'PAR';

interface MarcacionPs {
  idMarcacion: number;
  operacion: string;
  hora: string;
  estado: string | null;
}

interface Props {
  open: boolean;
  registro: RegistroMarcacion | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const CorregirMarcacionModal: React.FC<Props> = ({ open, registro, onClose, onSuccess }) => {
  const api = useGestionMarcacionApi();
  const [modo, setModo] = useState<Modo>('NORMAL');
  const [horaSeleccionada, setHoraSeleccionada] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [mensajeExito, setMensajeExito] = useState('');
  const [error, setError] = useState('');
  // Indica que la última acción exitosa envió la pareja de marcaciones al motor
  // de procesamiento de horas. No aplica a Crear Lápiz.
  const [entroACola, setEntroACola] = useState(false);

  // Corregir par
  const [cargandoPar, setCargandoPar] = useState(false);
  const [marcacionesPs, setMarcacionesPs] = useState<MarcacionPs[]>([]);
  const [seleccion, setSeleccion] = useState<number[]>([]);
  // Descarta respuestas de "Corregir par" que llegan después de cambiar de registro.
  const cargaParId = useRef(0);

  const esSinEntrada = String(registro?.estado || '').toUpperCase().trim() === 'SIN-ENTRADA';
  const tipoMarcacion = esSinEntrada ? 'ENTRADA' : 'SALIDA';
  const palabra = esSinEntrada ? 'Entrada' : 'Salida';

  const limpiar = () => {
    setModo('NORMAL');
    setHoraSeleccionada(null);
    setMensajeExito('');
    setError('');
    setEntroACola(false);
    setMarcacionesPs([]);
    setSeleccion([]);
  };

  // Al abrir con otro registro arrancamos de cero.
  useEffect(() => {
    cargaParId.current += 1;
    setCargandoPar(false);
    if (open) limpiar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, registro?.idMarcacion]);

  const parOrdenado = useMemo(() => {
    if (seleccion.length !== 2) return null;
    const a = marcacionesPs.find((m) => m.idMarcacion === seleccion[0]);
    const b = marcacionesPs.find((m) => m.idMarcacion === seleccion[1]);
    if (!a || !b) return null;
    // Sin cruce de medianoche: menor hora = entrada, mayor hora = salida.
    return String(a.hora).localeCompare(String(b.hora)) <= 0
      ? { entrada: a, salida: b }
      : { entrada: b, salida: a };
  }, [seleccion, marcacionesPs]);

  const handleClose = () => {
    if (loading) return;
    limpiar();
    onClose();
  };

  if (!registro) return null;

  // Tras una gestión exitosa se bloquean las acciones para evitar un segundo envío
  // (marcación duplicada o lápiz sobre un caso ya corregido).
  const bloqueado = loading || !!mensajeExito;

  const fechaISO = toISODate(registro.fecha);
  const horaPayload = horaSeleccionada ? horaSeleccionada.format('HH:mm:ss') : '00:00:00';

  const exito = (mensaje: string, cola: boolean) => {
    setMensajeExito(mensaje);
    setEntroACola(cola);
    marcarGestionEnGestionErrores({
      tipo_error: esSinEntrada ? 'SIN-ENTRADA' : 'SIN-SALIDA',
      nombrePServicio: registro.nombrePServicio,
      nroLegajo: registro.nroLegajo,
      fecha: fechaISO,
    });
    onSuccess?.();
  };

  // Envuelve cada acción con el mismo manejo de loading/errores.
  const ejecutar = async (accion: () => Promise<any>, okMsg: string, cola: boolean, errMsg: string) => {
    if (!fechaISO) {
      setError('La fecha del registro no es válida.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response: any = await accion();
      if (response?.status === 200) {
        exito(response.data?.info || okMsg, cola);
      } else {
        setError(errMsg);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || errMsg);
    } finally {
      setLoading(false);
    }
  };

  const guardarHora = () => ejecutar(
    () => {
      const payload = {
        tipoMarcacion,
        idMarcacion: registro.idMarcacion,
        psId: registro.psId,
        fecha: fechaISO,
        legajo: registro.nroLegajo,
        puntoServicio: registro.nombrePServicio,
        hora: horaPayload,
      };
      return esSinEntrada ? api.guardarEntrada(payload) : api.guardarSalida(payload);
    },
    `${palabra} guardada correctamente`,
    true,
    `No se pudo guardar la ${palabra.toLowerCase()}.`,
  );

  const crearLapiz = () => ejecutar(
    () => {
      const payload: any = {
        nroLegajo: registro.nroLegajo,
        fecha: fechaISO,
        idMarcacion: registro.idMarcacion,
        nombrePServicio: registro.nombrePServicio,
        psId: registro.psId,
      };
      if (esSinEntrada) payload.entrada = horaPayload;
      else payload.salida = horaPayload;
      return esSinEntrada ? api.gestionarSinEntrada(payload) : api.gestionarSinSalida(payload);
    },
    'Lápiz creado correctamente',
    false,
    'No se pudo crear el lápiz.',
  );

  // Cambia el CodOperacion de la marcación huérfana (HS -> HE o HE -> HS).
  const cambiarTipo = () => ejecutar(
    () => api.modificarMarcacion({ tipoMarcacion, idMarcacion: registro.idMarcacion }),
    `Marcación cambiada a ${palabra}`,
    true,
    'No se pudo cambiar la marcación.',
  );

  const corregirPar = () => {
    if (!parOrdenado) {
      setError('Seleccioná exactamente 2 marcaciones.');
      return;
    }
    return ejecutar(
      () => api.emparejarMarcaciones({
        entrada_id: parOrdenado.entrada.idMarcacion,
        salida_id: parOrdenado.salida.idMarcacion,
      }),
      'Se corrigió el par.',
      true,
      'No se pudo corregir el par.',
    );
  };

  const cargarMarcacionesPs = async () => {
    if (!registro.psId) {
      setError('No se pudo identificar el Punto de Servicio para corregir el par.');
      return;
    }
    const id = ++cargaParId.current;
    setCargandoPar(true);
    setError('');
    const res = await api.traerMarcacionesPs(registro.nroLegajo, fechaISO, registro.psId);
    if (id !== cargaParId.current) return;
    if (res.ok && res.data) {
      const normalizadas: MarcacionPs[] = res.data
        .map((x: any) => ({
          idMarcacion: Number(x?.idMarcacion),
          operacion: String(x?.operacion || '').trim(),
          hora: String(x?.hora || '').trim(),
          estado: x?.estado ?? null,
        }))
        .filter((x: MarcacionPs) => Number.isFinite(x.idMarcacion) && x.idMarcacion > 0)
        .sort((a: MarcacionPs, b: MarcacionPs) => a.hora.localeCompare(b.hora));
      setMarcacionesPs(normalizadas);
      if (normalizadas.length === 0) setError('No se encontraron marcaciones para ese día/PS.');
    } else {
      setError(res.error || 'No se pudieron cargar las marcaciones.');
    }
    setCargandoPar(false);
  };

  const cambiarModo = (nuevo: Modo) => {
    setModo(nuevo);
    setError('');
    if (nuevo === 'PAR' && marcacionesPs.length === 0 && !cargandoPar) {
      cargarMarcacionesPs();
    }
  };

  const toggleSeleccion = (id: number) => {
    setSeleccion((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return prev;
      return [...prev, id];
    });
  };

  const estiloModo = (activo: boolean) => (activo
    ? { backgroundColor: '#900533', color: 'white', borderColor: '#900533', textTransform: 'none' as const, fontWeight: 700 }
    : { color: '#900533', borderColor: '#900533', textTransform: 'none' as const, fontWeight: 700 });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#900533', color: 'white' }}>
        <Typography variant="h6" style={{ color: 'white' }}>Gestión Marcación · {palabra}</Typography>
        <IconButton onClick={handleClose} size="small" style={{ color: 'white' }} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gridGap={8} marginBottom={2}>
          <Typography variant="body2"><strong>Operario:</strong> {registro.nombre}</Typography>
          <Typography variant="body2"><strong>Legajo:</strong> {registro.nroLegajo}</Typography>
          <Typography variant="body2"><strong>Punto de Servicio:</strong> {registro.nombrePServicio}</Typography>
          <Typography variant="body2"><strong>Fecha:</strong> {fechaISO || registro.fecha}</Typography>
        </Box>

        <ButtonGroup fullWidth disabled={bloqueado}>
          <Button variant={modo === 'NORMAL' ? 'contained' : 'outlined'} style={estiloModo(modo === 'NORMAL')} onClick={() => cambiarModo('NORMAL')}>
            Gestión normal
          </Button>
          <Button variant={modo === 'PAR' ? 'contained' : 'outlined'} style={estiloModo(modo === 'PAR')} onClick={() => cambiarModo('PAR')}>
            Corregir par
          </Button>
        </ButtonGroup>

        {modo === 'NORMAL' && (
          <>
            <MarcacionesDiaPanel
              legajo={registro.nroLegajo}
              fecha={registro.fecha}
              idMarcacionActual={registro.idMarcacion}
            />

            <Box marginY={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  ampm={false}
                  label={`Hora de ${palabra}`}
                  value={horaSeleccionada}
                  onChange={(newTime) => setHoraSeleccionada(newTime as Dayjs | null)}
                />
              </LocalizationProvider>
            </Box>
          </>
        )}

        {modo === 'PAR' && (
          <Box marginTop={2}>
            <Typography variant="body2" style={{ color: '#666', marginBottom: 8 }}>
              Seleccioná exactamente 2 marcaciones del día en este PS. La menor hora se toma como Entrada y la mayor como Salida.
            </Typography>
            {cargandoPar && (
              <Box display="flex" alignItems="center" gridGap={8}>
                <CircularProgress size={16} style={{ color: '#900533' }} />
                <Typography variant="caption">Cargando marcaciones...</Typography>
              </Box>
            )}
            {marcacionesPs.map((m) => (
              <Box
                key={m.idMarcacion}
                display="flex"
                alignItems="center"
                gridGap={8}
                style={{ border: '1px solid #e0e0e0', borderRadius: 4, marginBottom: 6, cursor: 'pointer' }}
                onClick={() => toggleSeleccion(m.idMarcacion)}
              >
                <Checkbox
                  checked={seleccion.includes(m.idMarcacion)}
                  size="small"
                  style={{ color: '#900533' }}
                />
                <span style={{ fontWeight: 600, minWidth: 30 }}>{m.operacion || '-'}</span>
                <span style={{ minWidth: 50 }}>{m.hora}</span>
                <span style={{ fontSize: 12, color: '#666' }}>{m.estado || 'PROCESADA'}</span>
              </Box>
            ))}
            {parOrdenado && (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                <strong>Par seleccionado:</strong> Entrada {parOrdenado.entrada.hora} / Salida {parOrdenado.salida.hora}
              </Typography>
            )}
          </Box>
        )}

        {mensajeExito && (
          <Box display="flex" alignItems="center" gridGap={8} marginTop={2}>
            <DoneAllOutlinedIcon style={{ color: '#900533' }} />
            <Typography style={{ color: '#900533' }}>{mensajeExito}</Typography>
          </Box>
        )}
        {entroACola && (
          <Box
            display="flex"
            alignItems="flex-start"
            gridGap={8}
            marginTop={2}
            padding={1.5}
            style={{ background: '#e8f4fd', border: '1px solid #a5c8e9', borderRadius: 6 }}
          >
            <InfoOutlinedIcon style={{ color: '#0d5fa6', marginTop: 2 }} fontSize="small" />
            <Typography variant="body2" style={{ color: '#0d5fa6' }}>
              La pareja de marcaciones entró a la <strong>cola del proceso de horas</strong>. Esperá unos minutos a que el motor las procese y luego verificá el resultado en la pestaña <strong>Procesadas</strong>.
            </Typography>
          </Box>
        )}
        {error && (
          <Box marginTop={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '8px 16px' }}>
        <Button onClick={handleClose} disabled={loading}>
          Cerrar
        </Button>
        {modo === 'NORMAL' ? (
          <Box display="flex" flexWrap="wrap" gridGap={8} justifyContent="flex-end">
            <Button variant="outlined" onClick={cambiarTipo} disabled={bloqueado}>
              Cambiar a {palabra}
            </Button>
            <Button variant="outlined" onClick={crearLapiz} disabled={bloqueado || !horaSeleccionada}>
              Crear Lápiz
            </Button>
            <Button
              variant="contained"
              onClick={guardarHora}
              disabled={bloqueado || !horaSeleccionada}
              style={{ backgroundColor: '#900533', color: 'white' }}
              startIcon={loading ? <CircularProgress size={16} style={{ color: 'white' }} /> : <SaveIcon />}
            >
              Guardar {palabra}
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            onClick={corregirPar}
            disabled={bloqueado || !parOrdenado}
            style={{ backgroundColor: '#900533', color: 'white' }}
            startIcon={loading ? <CircularProgress size={16} style={{ color: 'white' }} /> : <SaveIcon />}
          >
            Corregir par
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CorregirMarcacionModal;
